import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useToastr } from '@farmshare/ui-components';
import {
  EnumUserType,
  User,
  useUserBasicUpsertMutation,
  useUserHangTimePreferenceUpdateMutation,
} from '../../../lib/graphql';
import {
  CustomerFormValues,
  validateCustomerForm,
} from '../helpers/customer-validation';
import {
  buildUserRelationships,
  customerToFormValues,
} from '../helpers/customer-form-helpers';

interface UseCustomerFormProps {
  customer: User | null | undefined;
  forcedType?: EnumUserType;
  vendorId: string | undefined;
  onSuccess?: (created: User) => void | Promise<void>;
  onClose: () => void;
  deferMode?: boolean;
  onDeferSubmit?: (vals: CustomerFormValues) => void | Promise<void>;
  /**
   * The host modal's busy latch, when there is one. Held for the length of the
   * save so the dialog can't be closed over a request that is still in flight.
   */
  setBusy?: (busy: boolean) => void;
}

export function useCustomerForm({
  customer,
  forcedType,
  vendorId,
  onSuccess,
  onClose,
  deferMode = false,
  onDeferSubmit,
  setBusy,
}: UseCustomerFormProps) {
  const [upsertUser, { loading }] = useUserBasicUpsertMutation();
  const [updateHangTimePref] = useUserHangTimePreferenceUpdateMutation();
  const { push } = useToastr();

  const formik = useFormik<CustomerFormValues>({
    initialValues: customerToFormValues(customer, forcedType, vendorId),
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (vals) => {
      const errors = validateCustomerForm(vals);
      // In defer mode (embedded create-contact for scheduling), allow Buyer without producerId.
      if (
        deferMode &&
        errors?.relationships &&
        typeof errors.relationships === 'object' &&
        Object.prototype.hasOwnProperty.call(errors.relationships, 'producerId')
      ) {
        // Remove producerId error
        const { producerId: _producerId, ...restRelationships } =
          errors.relationships as Record<string, unknown>;
        // If no other relationship errors remain, remove relationships error entirely
        if (Object.keys(restRelationships).length === 0) {
          const { relationships: _relationships, ...restErrors } = errors;
          return restErrors;
        }
        return { ...errors, relationships: restRelationships };
      }
      return errors;
    },
    onSubmit: async (vals: CustomerFormValues) => {
      // The save runs in here, where the modal can't see it — so without this
      // the X, the backdrop and Escape would all close the dialog mid-save, and
      // reopening it to save again creates a duplicate customer.
      setBusy?.(true);
      try {
        if (deferMode) {
          await onDeferSubmit?.(vals);
          formik.resetForm();
          setBusy?.(false);
          onClose();
          return;
        }

        const relationships = buildUserRelationships(
          vals.type,
          vendorId,
          vals.relationships?.producerId,
        );

        const { data } = await upsertUser({
          variables: {
            userId: customer?._id,
            firstName: vals.first_name.trim(),
            lastName: vals.last_name.trim(),
            email: vals.email?.trim().replace(/["']/g, '').toLowerCase(),
            phone: vals.phone?.trim().replace(/["']/g, ''),
            company: vals.company?.trim(),
            address: {
              street: vals.address_street.trim(),
              street2: vals.address_street2.trim(),
              city: vals.address_city.trim(),
              state: vals.address_state.trim(),
              postcode: vals.address_postcode.trim(),
            },
            relationships,
          },
        });

        const payload = data?.userBasicUpsert as
          | { user?: User | null; message?: string | null }
          | undefined;
        const returnedUser = payload?.user as User | undefined;
        if (payload?.message) {
          const msg = payload.message;
          const lower = msg.toLowerCase();
          // If message indicates an already-existing relationship, treat as informational
          const isInfo =
            lower.includes('already exists') &&
            (lower.includes('relationship to') ||
              lower.includes('relationship with')) &&
            !lower.includes('has been added');

          push({
            title: isInfo ? 'Info' : 'Success',
            body: msg,
            bg: isInfo ? 'info' : 'success',
            delay: 5000,
          });
        }
        // Save hang time preferences if changed
        const userId = returnedUser?._id ?? customer?._id;
        if (userId && vendorId && vals.hangTimePreferences) {
          for (const [species, daysStr] of Object.entries(
            vals.hangTimePreferences,
          )) {
            // `0` is a valid same-day cut preference; `null`/`undefined`/`''`
            // clears the preference. The mutation's `preferredDays` is typed
            // Int, so we must coerce — the form stores the chip value as a
            // string and passing it through fails server validation ("Int
            // cannot represent non-integer value"). Type says `number` but
            // runtime is string (see customer-form-helpers).
            const raw = daysStr as unknown as
              | number
              | string
              | null
              | undefined;
            const parsed = raw == null || raw === '' ? null : Number(raw);
            // Server schema is `Int`; a non-integer like 21.5 would pass
            // `isFinite` but be rejected GraphQL-side. The source UI is a
            // chip with integer durations, so anything fractional is an
            // upstream bug — skip rather than silently truncating.
            if (
              parsed != null &&
              (!Number.isFinite(parsed) || !Number.isInteger(parsed))
            ) {
              continue;
            }
            const days = parsed;
            await updateHangTimePref({
              variables: {
                userId,
                vendorId,
                species,
                preferredDays: days,
              },
              refetchQueries: ['ProcessorCustomerById'],
              awaitRefetchQueries: true,
            });
          }
        }

        if (returnedUser) {
          onSuccess?.(returnedUser);
        }
        formik.resetForm();
        // Released before `onClose`, or the modal refuses the close.
        setBusy?.(false);
        onClose();
      } catch (error) {
        setBusy?.(false);
        let message = (error as Error).message || 'Something went wrong';

        const lower = message.toLowerCase();
        if (lower.includes('validation for format email')) {
          message = 'Enter a valid email address';
        } else if (
          lower.includes('duplicate key') ||
          (lower.includes('already exists') && !lower.includes('relationship'))
        ) {
          // Preserve specific relationship errors from the API; only fall back to generic duplicate when not relationship-related
          message = 'A user with this email or phone already exists.';
        }

        push({
          title: 'Error',
          body: message,
          bg: 'danger',
          delay: 5000,
        });
      }
    },
  });

  // Sync forced type with form
  useEffect(() => {
    if (forcedType && formik.values.type !== forcedType) {
      formik.setFieldValue('type', forcedType);
    }
  }, [forcedType]);

  // Reset producerId when user type changes to non-buyer
  useEffect(() => {
    if (
      formik.values.type !== EnumUserType.Buyer &&
      formik.values.relationships?.producerId
    ) {
      formik.setFieldValue('relationships.producerId', undefined);
    }
  }, [formik.values.type]);

  // Helper functions
  const shouldShowError = <Key extends keyof CustomerFormValues>(name: Key) =>
    Boolean(formik.touched[name]) || formik.submitCount > 0;

  const fieldProps = <Key extends keyof CustomerFormValues>(name: Key) => {
    const err = formik.errors[name];
    const errMessage = typeof err === 'string' ? err : undefined;
    return {
      ...formik.getFieldProps(name),
      error: Boolean(shouldShowError(name) && err),
      helperText: (shouldShowError(name) && errMessage) || undefined,
    };
  };

  const cancelForm = () => {
    formik.resetForm();
    onClose();
  };

  return {
    formik,
    loading,
    shouldShowError,
    fieldProps,
    cancelForm,
  };
}
